import {isResponsiveBreakpoint, isTouchDevice} from "./responsive";
import getConfig from "./config";

const config = getConfig();

class Navbar {
    static getNavBarHeight(
        includeAdminBar = true,
        onlyVisibleHeight = false,
        round = true,
    ) {
        if (!config.masthead) {
            return 0;
        }

        let height =
            onlyVisibleHeight && config.masthead.classList.contains("is-hidden")
                ? 0
                : config.masthead.getBoundingClientRect().height;

        if (includeAdminBar && config.wpAdminBar) {
            height += config.wpAdminBar.getBoundingClientRect().height;
        }

        if (round) {
            height = Math.round(height);
        }

        return height;
    }

    static setupNavbarToggler() {
        if (!config.navbarToggler || !config.mainNav) {
            return;
        }

        config.navbarToggler.addEventListener("click", (e) => {
            e.stopPropagation();
            const active = !config.navbarToggler.classList.contains(
                "navbar-toggler--active",
            );
            config.navbarToggler.classList.toggle("navbar-toggler--active");
            config.navbarToggler.ariaExpanded = active ? "true" : "false";
            config.mainNav.classList.toggle("navbar-menu--open");
            document.documentElement.classList.toggle("menu-open");
            document.body.classList.toggle("menu-open");
            this.updateMainNavLinksTabIndexes(config.mainNav);

            if (active) {
                const first_link = config.mainNav.querySelector("a");
                if (first_link) {
                    first_link.focus();
                }
            } else {
                document.documentElement.classList.remove("submenu-open");
                document.body.classList.remove("submenu-open");
            }
        });

        let mainNavSelector = config.mainNav.getAttribute("id") || null;
        if (mainNavSelector) {
            mainNavSelector = `#${mainNavSelector}`;
        } else if (0 < config.mainNav.classList.length) {
            mainNavSelector = `.${[...config.mainNav.classList].join(".")}`;
        }

        document.addEventListener("click", (e) => {
            if (
                document.documentElement.classList.contains("menu-open") &&
                !e.target.closest(mainNavSelector)
            ) {
                config.mainNav.addEventListener(
                    "transitionend",
                    () => this.transitionEnd(),
                    {once: true},
                );
                config.mainNav.classList.remove("navbar-menu--open", "show");
            }
        });
    }

    static transitionEnd() {
        document.documentElement.classList.remove("menu-open");
        document.body.classList.remove("menu-open");
        config.navbarToggler.ariaExpanded = false;
        config.navbarToggler.classList.remove(
            "navbar-toggler--active",
            "active",
        );
        this.updateMainNavLinksTabIndexes();
    }

    static updateNavBarHeight() {
        document.documentElement.style.setProperty(
            config.prefix + "navbar-height",
            this.getNavBarHeight(false) + "px",
        );
        return this.getNavBarHeight();
    }

    static updateMainNavLinksTabIndexes() {
        if (!config.mainNav) {
            return;
        }

        const newTabIndex =
            isResponsiveBreakpoint(config.desktopBp) ||
            config.mainNav.classList.contains("navbar-menu--open")
                ? 0
                : -1;
        [...config.mainNav.querySelectorAll("a")].forEach(
            (link) => (link.tabIndex = newTabIndex),
        );
    }

    static setupDropdownToggles() {
        if (!config.mainNav) {
            return;
        }

        const dropdownToggles = [
            ...config.mainNav.querySelectorAll("a.dropdown-toggle"),
        ];
        dropdownToggles.forEach((targetToggle) => {
            const parentItem = targetToggle.parentElement;
            const dropdownMenuWrap = parentItem.querySelector(
                ".dropdown-menu-wrap",
            );
            const dropdownMenu = parentItem.querySelector(".dropdown-menu");

            targetToggle.addEventListener("mousedown", (e) => {
                if (isTouchDevice()) {
                    return;
                }

                e.preventDefault();
            });
            parentItem.addEventListener("mouseenter", () => {
                if (isTouchDevice()) {
                    return;
                }

                targetToggle.classList.add("show");
                targetToggle.ariaExpanded = "true";
                dropdownMenuWrap.classList.add("show");
                dropdownMenu.classList.add("show");
                document.documentElement.classList.add("submenu-open");
                document.body.classList.add("submenu-open");
            });
            parentItem.addEventListener("mouseleave", () => {
                if (isTouchDevice()) {
                    return;
                }

                targetToggle.classList.remove("show");
                targetToggle.ariaExpanded = "false";
                dropdownMenuWrap.classList.remove("show");
                dropdownMenu.classList.remove("show");
                document.documentElement.classList.remove("submenu-open");
                document.body.classList.remove("submenu-open");
            });
            targetToggle.addEventListener("click", (e) => {
                e.preventDefault();
                targetToggle.classList.toggle("show");
                const isShowing = targetToggle.classList.contains("show");

                if (isShowing) {
                    targetToggle.ariaExpanded = "true";
                    dropdownMenuWrap.classList.add("show");
                    dropdownMenu.classList.add("show");
                    document.documentElement.classList.add("submenu-open");
                    document.body.classList.add("submenu-open");
                } else {
                    targetToggle.ariaExpanded = "false";
                    dropdownMenuWrap.classList.remove("show");
                    dropdownMenu.classList.remove("show");
                    document.documentElement.classList.remove("submenu-open");
                    document.body.classList.remove("submenu-open");
                }

                // On desktop, close all other dropdown menus
                if (
                    window.innerWidth >= config.responsiveBps[config.desktopBp]
                ) {
                    dropdownToggles.forEach((siblingToggle) => {
                        if (siblingToggle === targetToggle) {
                            return;
                        }

                        const siblingDropdownMenuWrap =
                            siblingToggle.parentElement.querySelector(
                                ".dropdown-menu-wrap",
                            );
                        const siblingDropdownMenu =
                            siblingToggle.parentElement.querySelector(
                                ".dropdown-menu",
                            );
                        siblingToggle.classList.remove("show");
                        siblingToggle.ariaExpanded = "false";
                        siblingDropdownMenuWrap.classList.remove("show");
                        siblingDropdownMenu.classList.remove("show");
                    });
                }
            });
        });
        document.addEventListener("click", (e) => {
            if (window.innerWidth < config.responsiveBps[config.desktopBp]) {
                return;
            }

            if (
                !e.target.closest(".dropdown-menu") &&
                !e.target.closest(".dropdown-toggle")
            ) {
                [...document.querySelectorAll(".dropdown-toggle.show")].forEach(
                    (toggle) => toggle.classList.remove("show"),
                );
                [
                    ...document.querySelectorAll(".dropdown-menu-wrap.show"),
                ].forEach((menuWrap) => menuWrap.classList.remove("show"));
                [...document.querySelectorAll(".dropdown-menu.show")].forEach(
                    (menu) => menu.classList.remove("show"),
                );
                document.documentElement.classList.remove("submenu-open");
                document.body.classList.remove("submenu-open");
            }
        });
    }

    static setupNavLinks() {
        if (!config.mainNav) {
            return;
        }

        const navLinks = [
            ...config.mainNav.querySelectorAll(
                ":where(.nav-link, .dropdown-item)[aria-current=page]",
            ),
        ];
        navLinks.forEach((navLink) => {
            const url = new URL(navLink.href);

            if (
                url.origin === window.location.origin &&
                url.pathname === window.location.pathname &&
                url.hash !== window.location.hash
            ) {
                navLink.removeAttribute("aria-current");
                navLink.parentElement.classList.remove(
                    "current-menu-item",
                    "current-menu-ancestor",
                );
            }
        });
    }

    static setupDropdownItems() {
        if (!config.mainNav) {
            return;
        }

        const dropdownItems = [
            ...config.mainNav.querySelectorAll(".navbar-nav > .dropdown"),
        ];
        dropdownItems.forEach((dropdownItem) => {
            dropdownItem.addEventListener("mouseenter", function () {
                if (isTouchDevice()) {
                    return;
                }

                document.documentElement.classList.add("submenu-open");
                document.body.classList.add("submenu-open");
            });
        });
        config.mainNav.addEventListener("mouseleave", function () {
            if (isTouchDevice()) {
                return;
            }

            document.documentElement.classList.remove("submenu-open");
            document.body.classList.remove("submenu-open");
        });
    }
}

export default Navbar;
